/**
 * UserModerationPanel Component
 * Interface for managing user messaging restrictions and moderation history
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Pagination,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faBan,
  faUnlock,
  faSearch,
  faHistory,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import UserAvatar from './UserAvatar';
import { formatDistanceToNow } from '../../utils/dateUtils';
import './UserModerationPanel.css';

/**
 * UserModerationPanel - Manage user messaging restrictions
 * @param {Object} props - Component props
 * @param {Object} props.user - Current admin user
 * @returns {JSX.Element} UserModerationPanel component
 */
const UserModerationPanel = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [restrictionData, setRestrictionData] = useState({
    durationType: 'temporary',
    duration: '7',
    reason: ''
  });
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * Load users with moderation status
   */
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call
      // const data = await getUsersWithModerationStatus({
      //   page: currentPage,
      //   search: searchQuery,
      //   status: filterStatus
      // });

      // Mock data for development
      const mockUsers = [
        {
          userId: 'user-123',
          displayName: 'John Doe',
          email: 'john.doe@example.com',
          photoUrl: null,
          role: 'speaker',
          messagingRestricted: false,
          restrictionEndDate: null,
          violationCount: 0,
          lastViolation: null,
          createdAt: new Date(Date.now() - 86400000 * 30).toISOString()
        },
        {
          userId: 'user-456',
          displayName: 'Jane Smith',
          email: 'jane.smith@example.com',
          photoUrl: null,
          role: 'attendee',
          messagingRestricted: true,
          restrictionEndDate: new Date(Date.now() + 86400000 * 5).toISOString(),
          restrictionReason: 'Inappropriate content in messages',
          violationCount: 2,
          lastViolation: new Date(Date.now() - 3600000).toISOString(),
          createdAt: new Date(Date.now() - 86400000 * 60).toISOString()
        },
        {
          userId: 'user-789',
          displayName: 'Bob Johnson',
          email: 'bob.johnson@example.com',
          photoUrl: null,
          role: 'speaker',
          messagingRestricted: false,
          restrictionEndDate: null,
          violationCount: 1,
          lastViolation: new Date(Date.now() - 86400000 * 15).toISOString(),
          createdAt: new Date(Date.now() - 86400000 * 90).toISOString()
        }
      ];

      // Filter based on search and status
      let filteredUsers = mockUsers;
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredUsers = filteredUsers.filter(u =>
          u.displayName.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query)
        );
      }

      if (filterStatus === 'restricted') {
        filteredUsers = filteredUsers.filter(u => u.messagingRestricted);
      } else if (filterStatus === 'unrestricted') {
        filteredUsers = filteredUsers.filter(u => !u.messagingRestricted);
      }

      setUsers(filteredUsers);
      setTotalPages(1);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [currentPage, filterStatus]);

  /**
   * Handle search
   */
  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers();
  };

  /**
   * Handle restricting a user
   */
  const handleRestrictUser = (user) => {
    setSelectedUser(user);
    setRestrictionData({
      durationType: 'temporary',
      duration: '7',
      reason: ''
    });
    setShowRestrictionModal(true);
  };

  /**
   * Handle unrestricting a user
   */
  const handleUnrestrictUser = async (user) => {
    if (!window.confirm(`Are you sure you want to remove messaging restrictions for ${user.displayName}?`)) {
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // TODO: Replace with actual API call
      // await removeUserRestriction(user.userId);

      console.log('Removing restriction for user:', user.userId);

      // Update local state
      setUsers(users.map(u =>
        u.userId === user.userId
          ? { ...u, messagingRestricted: false, restrictionEndDate: null }
          : u
      ));
    } catch (err) {
      console.error('Error removing restriction:', err);
      setError(err.message || 'Failed to remove restriction');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Handle applying restriction
   */
  const handleApplyRestriction = async () => {
    if (!selectedUser || !restrictionData.reason.trim()) {
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const restrictionPayload = {
        userId: selectedUser.userId,
        reason: restrictionData.reason.trim(),
        durationType: restrictionData.durationType
      };

      if (restrictionData.durationType === 'temporary') {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + parseInt(restrictionData.duration));
        restrictionPayload.restrictionEndDate = endDate.toISOString();
      }

      // TODO: Replace with actual API call
      // await restrictUserMessaging(restrictionPayload);

      console.log('Applying restriction:', restrictionPayload);

      // Update local state
      setUsers(users.map(u =>
        u.userId === selectedUser.userId
          ? {
              ...u,
              messagingRestricted: true,
              restrictionEndDate: restrictionPayload.restrictionEndDate || null,
              restrictionReason: restrictionData.reason.trim()
            }
          : u
      ));

      setShowRestrictionModal(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error applying restriction:', err);
      setError(err.message || 'Failed to apply restriction');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Handle viewing moderation history
   */
  const handleViewHistory = (user) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
  };

  /**
   * Get status badge
   */
  const getStatusBadge = (user) => {
    if (user.messagingRestricted) {
      return <Badge bg="danger">Restricted</Badge>;
    }
    if (user.violationCount > 0) {
      return <Badge bg="warning">Warnings</Badge>;
    }
    return <Badge bg="success">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="user-moderation-panel">
      <div className="moderation-header">
        <h3>
          <FontAwesomeIcon icon={faUsers} className="me-2" />
          User Moderation
        </h3>
        <p className="text-muted">
          Manage user messaging restrictions and review moderation history
        </p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filter */}
      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <div className="d-flex gap-2">
              <InputGroup className="flex-grow-1">
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="all">All Users</option>
                <option value="restricted">Restricted</option>
                <option value="unrestricted">Unrestricted</option>
              </Form.Select>
              <Button type="submit" variant="primary">
                Search
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Users Table */}
      {users.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
            <h5>No Users Found</h5>
            <p className="text-muted">
              {searchQuery
                ? 'No users match your search criteria'
                : 'No users to display'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Table responsive hover className="moderation-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Violations</th>
                <th>Last Violation</th>
                <th>Restriction Ends</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.userId}>
                  <td>
                    <div className="d-flex align-items-center">
                      <UserAvatar
                        user={{ displayName: u.displayName, photoUrl: u.photoUrl }}
                        size="sm"
                      />
                      <div className="ms-2">
                        <div className="fw-bold">{u.displayName}</div>
                        <div className="text-muted small">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge bg="secondary">{u.role}</Badge>
                  </td>
                  <td>{getStatusBadge(u)}</td>
                  <td>
                    {u.violationCount > 0 ? (
                      <Badge bg="warning">{u.violationCount}</Badge>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </td>
                  <td className="text-muted">
                    {u.lastViolation
                      ? formatDistanceToNow(u.lastViolation)
                      : 'Never'}
                  </td>
                  <td>
                    {u.messagingRestricted && u.restrictionEndDate ? (
                      <span className="text-danger">
                        {formatDistanceToNow(u.restrictionEndDate)}
                      </span>
                    ) : u.messagingRestricted ? (
                      <span className="text-danger">Permanent</span>
                    ) : (
                      <span className="text-muted">N/A</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewHistory(u)}
                        title="View history"
                      >
                        <FontAwesomeIcon icon={faHistory} />
                      </Button>
                      {u.messagingRestricted ? (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleUnrestrictUser(u)}
                          title="Remove restriction"
                          disabled={processing}
                        >
                          <FontAwesomeIcon icon={faUnlock} />
                        </Button>
                      ) : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRestrictUser(u)}
                          title="Restrict messaging"
                        >
                          <FontAwesomeIcon icon={faBan} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                {[...Array(totalPages)].map((_, i) => (
                  <Pagination.Item
                    key={i + 1}
                    active={i + 1 === currentPage}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Restriction Modal */}
      <Modal
        show={showRestrictionModal}
        onHide={() => setShowRestrictionModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faBan} className="me-2" />
            Restrict User Messaging
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <Alert variant="warning">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                This will prevent <strong>{selectedUser.displayName}</strong> from sending messages.
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Restriction Duration</Form.Label>
                  <Form.Check
                    type="radio"
                    id="duration-temporary"
                    label="Temporary"
                    name="durationType"
                    checked={restrictionData.durationType === 'temporary'}
                    onChange={() =>
                      setRestrictionData({ ...restrictionData, durationType: 'temporary' })
                    }
                  />
                  {restrictionData.durationType === 'temporary' && (
                    <Form.Select
                      className="mt-2"
                      value={restrictionData.duration}
                      onChange={(e) =>
                        setRestrictionData({ ...restrictionData, duration: e.target.value })
                      }
                    >
                      <option value="1">1 day</option>
                      <option value="3">3 days</option>
                      <option value="7">7 days</option>
                      <option value="14">14 days</option>
                      <option value="30">30 days</option>
                    </Form.Select>
                  )}
                  <Form.Check
                    type="radio"
                    id="duration-permanent"
                    label="Permanent"
                    name="durationType"
                    className="mt-2"
                    checked={restrictionData.durationType === 'permanent'}
                    onChange={() =>
                      setRestrictionData({ ...restrictionData, durationType: 'permanent' })
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Reason (required)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={restrictionData.reason}
                    onChange={(e) =>
                      setRestrictionData({ ...restrictionData, reason: e.target.value })
                    }
                    placeholder="Provide a detailed reason for this restriction..."
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRestrictionModal(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleApplyRestriction}
            disabled={processing || !restrictionData.reason.trim()}
          >
            {processing ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Applying...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faBan} className="me-2" />
                Apply Restriction
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* History Modal */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            Moderation History
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div className="moderation-history">
              <div className="user-info mb-3">
                <div className="d-flex align-items-center">
                  <UserAvatar
                    user={{
                      displayName: selectedUser.displayName,
                      photoUrl: selectedUser.photoUrl
                    }}
                    size="md"
                  />
                  <div className="ms-3">
                    <h5 className="mb-0">{selectedUser.displayName}</h5>
                    <p className="text-muted mb-0">{selectedUser.email}</p>
                  </div>
                </div>
              </div>

              <div className="history-summary mb-3">
                <Card>
                  <Card.Body>
                    <div className="row text-center">
                      <div className="col-4">
                        <h4 className="mb-0">{selectedUser.violationCount}</h4>
                        <small className="text-muted">Violations</small>
                      </div>
                      <div className="col-4">
                        <h4 className="mb-0">
                          {selectedUser.messagingRestricted ? 'Yes' : 'No'}
                        </h4>
                        <small className="text-muted">Restricted</small>
                      </div>
                      <div className="col-4">
                        <h4 className="mb-0">
                          {selectedUser.lastViolation
                            ? formatDistanceToNow(selectedUser.lastViolation)
                            : 'Never'}
                        </h4>
                        <small className="text-muted">Last Violation</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <h6>Recent Activity</h6>
              <div className="history-timeline">
                {/* TODO: Load actual moderation history */}
                <p className="text-muted">No moderation history available.</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserModerationPanel;
